import {
    ProtocolConsumptionSyncAnalysisState,
    ProtocolConsumptionSyncAnalysisUnloadState,
} from '../../states/ProtocolConsumptionSyncAnalysis.States';
import * as actions from '../../actions/ProtocolConsumptionSyncAnalysis.Actions';
import { SyncAnalysis } from '../../../models/Entities/Protocols/SyncAnalysis';
import { SyncAnalysisStatusEnum } from '../../../models/Enums/SyncAnalysisStatusEnum';

const isRunning = (analysis?: SyncAnalysis | null): boolean =>
    analysis?.status === SyncAnalysisStatusEnum.Pending ||
    analysis?.status === SyncAnalysisStatusEnum.InProgress;

const executedAtMs = (analysis?: SyncAnalysis | null): number | undefined => {
    if (!analysis?.executedAt) return undefined;

    const ms = new Date(analysis.executedAt).getTime();
    return Number.isNaN(ms) ? undefined : ms;
};

/**
 * El sondeo lanza una lectura cada pocos segundos, así que una que salió antes
 * puede llegar después de otra más nueva. Aplicarla haría retroceder el panel, y
 * eso se descarta en los dos casos en que puede pasar.
 */
const arrivedLate = (stored: SyncAnalysis | null | undefined, incoming: SyncAnalysis | null): boolean => {
    if (!stored || !incoming) return false;

    // Mismo análisis: la lectura lo muestra corriendo cuando lo guardado ya está
    // resuelto, o sea que salió antes de que terminara.
    if (stored.id === incoming.id) return !isRunning(stored) && isRunning(incoming);

    // Otro análisis: sólo se acepta si es más nuevo. Una lectura que salió antes de
    // que la corrida reemplazara su análisis trae el anterior, ya vencido.
    const storedAt = executedAtMs(stored);
    const incomingAt = executedAtMs(incoming);

    return storedAt !== undefined && incomingAt !== undefined && incomingAt < storedAt;
};

export default function ProtocolConsumptionSyncAnalysisReducer(
    state: ProtocolConsumptionSyncAnalysisState | undefined,
    action: any
): ProtocolConsumptionSyncAnalysisState {
    const currentState = state ?? ProtocolConsumptionSyncAnalysisUnloadState;

    switch (action.type) {
        case actions.SYNCANALYSIS_GET_REQUEST:
            return {
                ...currentState,
                isGettingAnalysis: true,
                successGettingAnalysis: undefined,
                failOnGettingAnalysis: false,
                error: undefined,
            };
        case actions.SYNCANALYSIS_GET_SUCCESS:
            return {
                ...currentState,
                isGettingAnalysis: false,
                successGettingAnalysis: true,
                failOnGettingAnalysis: false,
                analysisBySyncProcessId: {
                    ...currentState.analysisBySyncProcessId,
                    // null es un valor con significado —la corrida no fue
                    // analizada— y se guarda como tal para no volver a pedirlo.
                    [action.syncProcessId]: action.analysis ?? null,
                },
            };
        case actions.SYNCANALYSIS_GET_FAILURE:
            return {
                ...currentState,
                isGettingAnalysis: false,
                successGettingAnalysis: false,
                failOnGettingAnalysis: true,
                error: action.error,
            };

        case actions.SYNCANALYSIS_REFRESH_SUCCESS: {
            // Sondeo de un análisis en curso: sólo escribe el análisis. No toca las
            // banderas de carga a propósito —se repite cada pocos segundos y haría
            // parpadear la pantalla— ni registra el fallo de una lectura suelta.
            if (arrivedLate(currentState.analysisBySyncProcessId[action.syncProcessId], action.analysis)) {
                return currentState;
            }

            return {
                ...currentState,
                analysisBySyncProcessId: {
                    ...currentState.analysisBySyncProcessId,
                    [action.syncProcessId]: action.analysis ?? null,
                },
            };
        }

        case actions.SYNCANALYSIS_EXECUTE_REQUEST:
            return {
                ...currentState,
                isExecutingAnalysis: true,
                successExecutingAnalysis: undefined,
                failOnExecutingAnalysis: false,
                executionSyncProcessId: action.syncProcessId,
                executionError: undefined,
            };
        case actions.SYNCANALYSIS_EXECUTE_SUCCESS:
            // El análisis producido no viaja en la respuesta —sólo su id—, así que
            // el estado lo actualiza la relectura que dispara el middleware.
            return {
                ...currentState,
                isExecutingAnalysis: false,
                successExecutingAnalysis: true,
                failOnExecutingAnalysis: false,
                executionSyncProcessId: action.syncProcessId,
                executionError: undefined,
            };
        case actions.SYNCANALYSIS_EXECUTE_FAILURE:
            return {
                ...currentState,
                isExecutingAnalysis: false,
                successExecutingAnalysis: false,
                failOnExecutingAnalysis: true,
                executionSyncProcessId: action.syncProcessId,
                executionError: action.error,
            };

        case actions.SYNCANALYSIS_GETFINDINGS_REQUEST:
            return {
                ...currentState,
                isGettingFindings: true,
                successGettingFindings: undefined,
                failOnGettingFindings: false,
                error: undefined,
            };
        case actions.SYNCANALYSIS_GETFINDINGS_SUCCESS:
            return {
                ...currentState,
                isGettingFindings: false,
                successGettingFindings: true,
                failOnGettingFindings: false,
                findingsByAnalysisId: {
                    ...currentState.findingsByAnalysisId,
                    [action.analysisId]: action.findings ?? [],
                },
                findingsPaginationByAnalysisId: {
                    ...currentState.findingsPaginationByAnalysisId,
                    [action.analysisId]: action.paginationState,
                },
            };
        case actions.SYNCANALYSIS_GETFINDINGS_FAILURE:
            return {
                ...currentState,
                isGettingFindings: false,
                successGettingFindings: false,
                failOnGettingFindings: true,
                error: action.error,
            };

        default:
            return currentState;
    }
}
